import {
  Box,
  Heading,
  Text,
  VStack,
  Image,
  SimpleGrid,
  HStack,
  Input,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getBrandValue } from "../brandConfig";
import { useState } from "react";
import { useMutation, gql } from "@apollo/client";

const CONTACT_IMAGE_URL = "/Photos/IMG_7800.jpg";

const SUBMIT_CONTACT_FORM = gql`
  mutation SubmitContactForm($input: SubmitContactFormInput!) {
    submitContactForm(input: $input) {
      id
      firstName
      lastName
      email
      phone
      message
      createdAt
    }
  }
`;

const NAVY = "#03177A";
const ORANGE = "#FF6600";
const BORDER_COLOR = "rgba(3, 23, 122, 0.25)";

const ContactUs = () => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    message: "",
  });

  const [submitForm, { loading }] = useMutation(SUBMIT_CONTACT_FORM);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.fullName || !formData.email || !formData.phone || !formData.message) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields before submitting.",
        duration: 6000,
        isClosable: true,
        position: "bottom",
        containerStyle: { marginBottom: "40px" },
        render: () => (
          <Box
            bg="#000000"
            color="#FFFFFF"
            border="1px solid rgba(255, 59, 48, 0.3)"
            borderRadius="16px"
            boxShadow="0 12px 35px rgba(0, 0, 0, 0.9)"
            px={10}
            py={5}
            minW="500px"
            maxW="700px"
            backdropFilter="blur(15px)"
          >
            <HStack spacing={5} align="center">
              <Box w="14px" h="14px" bg="#FF3B30" borderRadius="full" flexShrink={0} />
              <VStack align="start" spacing={2} flex={1}>
                <Text fontWeight="800" fontSize="20px" color="#FFFFFF">
                  Missing Fields
                </Text>
                <Text fontSize="16px" color="#AAC3FF" lineHeight="1.5" fontWeight="600">
                  Please fill in all required fields.
                </Text>
              </VStack>
            </HStack>
          </Box>
        ),
      });
      return;
    }

    // Split full name into first and last
    const nameParts = formData.fullName.trim().split(/\s+/);
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    try {
      const result = await submitForm({
        variables: {
          input: {
            firstName,
            lastName,
            email: formData.email,
            phone: formData.phone,
            message: formData.message,
            notificationEmails: ["office@provequityholdings.com.au"],
          },
        },
      });

      console.log("Submission successful:", result.data.submitContactForm);

      toast({
        title: "Message Sent Successfully",
        description:
          "Thank you for reaching out. We'll review your inquiry and be in touch within 24 hours.",
        duration: 8000,
        isClosable: true,
        position: "bottom",
        containerStyle: { marginBottom: "40px" },
        render: () => (
          <Box
            bg="#000000"
            color="#FFFFFF"
            border="1px solid rgba(255, 102, 0, 0.3)"
            borderRadius="16px"
            boxShadow="0 12px 35px rgba(0, 0, 0, 0.9)"
            px={10}
            py={5}
            minW="500px"
            maxW="700px"
            backdropFilter="blur(15px)"
          >
            <HStack spacing={5} align="center">
              <Box w="14px" h="14px" bg="#FF6600" borderRadius="full" flexShrink={0} />
              <VStack align="start" spacing={2} flex={1}>
                <Text fontWeight="800" fontSize="20px" color="#FFFFFF">
                  Message Sent
                </Text>
                <Text fontSize="16px" color="#AAC3FF" lineHeight="1.5" fontWeight="600">
                  We'll be in touch within 24 hours.
                </Text>
              </VStack>
            </HStack>
          </Box>
        ),
      });

      setFormData({ fullName: "", email: "", phone: "", message: "" });
    } catch (err) {
      console.error("Submission failed:", err);
      toast({
        title: "Connection Error",
        description:
          "We're having trouble processing your request. Please check your connection and try again.",
        duration: 8000,
        isClosable: true,
        position: "bottom",
        containerStyle: { marginBottom: "40px" },
        render: () => (
          <Box
            bg="#000000"
            color="#FFFFFF"
            border="1px solid rgba(255, 59, 48, 0.3)"
            borderRadius="16px"
            boxShadow="0 12px 35px rgba(0, 0, 0, 0.9)"
            px={10}
            py={5}
            minW="500px"
            maxW="700px"
            backdropFilter="blur(15px)"
          >
            <HStack spacing={5} align="center">
              <Box w="14px" h="14px" bg="#FF3B30" borderRadius="full" flexShrink={0} />
              <VStack align="start" spacing={2} flex={1}>
                <Text fontWeight="800" fontSize="20px" color="#FFFFFF">
                  Connection Error
                </Text>
                <Text fontSize="16px" color="#AAC3FF" lineHeight="1.5" fontWeight="600">
                  Please check your connection and try again.
                </Text>
              </VStack>
            </HStack>
          </Box>
        ),
      });
    }
  };

  const inputStyles = {
    variant: "unstyled" as const,
    borderBottom: "1.5px solid",
    borderColor: BORDER_COLOR,
    borderRadius: "0",
    px: 0,
    py: 3,
    fontSize: "md",
    fontWeight: "500",
    color: NAVY,
    fontFamily: getBrandValue("fonts.body"),
    _placeholder: { color: NAVY, fontWeight: "500", opacity: 0.85 },
    _focus: { borderColor: ORANGE },
  };

  return (
    <Box
      bg="#FFFFFF"
      minHeight="100vh"
      display="flex"
      flexDirection="column"
    >
      <NavbarWithCallToAction />

      {/* Main Content */}
      <Box flex="1">
        <Box
          w="100%"
          pl="12vw"
          pr="5vw"
          pt="16vw"
          pb="10vw"
        >
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing="4vw"
            alignItems="start"
          >
            {/* Left: Form */}
            <Box maxW="38vw">
              <Heading
                as="h1"
                fontSize="clamp(1.8rem, 3.3vw, 3rem)"
                fontWeight="800"
                color={NAVY}
                fontFamily={getBrandValue("fonts.heading")}
                mb="2.5vw"
              >
                Contact Us
              </Heading>

              <form onSubmit={handleSubmit}>
                <VStack spacing={0} align="stretch">
                  <Input
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="Full Name*"
                    {...inputStyles}
                    mb="1.5vw"
                  />
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    type="tel"
                    placeholder="Phone Number*"
                    {...inputStyles}
                    mb="1.5vw"
                  />
                  <Input
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    type="email"
                    placeholder="Email Address*"
                    {...inputStyles}
                    mb="1.5vw"
                  />
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Message*"
                    variant="unstyled"
                    borderBottom="1.5px solid"
                    borderColor={BORDER_COLOR}
                    borderRadius="0"
                    px={0}
                    py={3}
                    fontSize="md"
                    fontWeight="500"
                    color={NAVY}
                    fontFamily={getBrandValue("fonts.body")}
                    _placeholder={{ color: NAVY, fontWeight: "500", opacity: 0.85 }}
                    _focus={{ borderColor: ORANGE }}
                    rows={5}
                    resize="none"
                    mb="2.5vw"
                  />

                  {/* Submit Button - same style as Home "Find out more" */}
                  <Box
                    as="button"
                    type="submit"
                    position="relative"
                    display="inline-flex"
                    alignItems="center"
                    alignSelf="flex-start"
                    bg="transparent"
                    pl={0}
                    pr={4}
                    py={2}
                    h="clamp(40px, 3.5vw, 48px)"
                    transition="all 0.3s ease"
                    cursor={loading ? "not-allowed" : "pointer"}
                    border="none"
                    role="group"
                    opacity={loading ? 0.6 : 1}
                    disabled={loading}
                    _hover={{
                      transform: loading ? "none" : "translateY(-2px)",
                    }}
                  >
                    <Box
                      position="absolute"
                      left="0"
                      top="50%"
                      transform="translateY(-50%)"
                      w="clamp(30px, 2.5vw, 36px)"
                      h="clamp(30px, 2.5vw, 36px)"
                      borderRadius="full"
                      bg={ORANGE}
                      transition="all 0.4s ease"
                      zIndex={0}
                      _groupHover={{
                        w: "100%",
                        borderRadius: "full",
                      }}
                    />
                    <Text
                      color={NAVY}
                      fontSize="clamp(0.9rem, 1.3vw, 1.1rem)"
                      fontWeight="700"
                      fontFamily={getBrandValue("fonts.body")}
                      position="relative"
                      zIndex={1}
                      pl="clamp(10px, 1vw, 12px)"
                      _groupHover={{
                        color: "#FFFFFF",
                      }}
                      transition="color 0.4s ease"
                    >
                      {loading ? "Sending..." : "Submit"}
                    </Text>
                  </Box>
                </VStack>
              </form>
            </Box>

            {/* Right: Image */}
            <Box
              display={{ base: "none", lg: "block" }}
              borderRadius="xl"
              overflow="hidden"
            >
              <Image
                src={CONTACT_IMAGE_URL}
                alt="Seniors enjoying a group exercise class"
                w="100%"
                h="40vw"
                objectFit="cover"
                borderRadius="xl"
              />
            </Box>
          </SimpleGrid>
        </Box>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default ContactUs;
